import { useState } from 'react';
import { useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/api';

interface CheckoutFormProps {
  bookingId: number;
  returnUrl: string;
  totalAmount: number;
  onPaymentSuccess?: () => void;
  onPaymentError?: (error: Error) => void;
}

export function CheckoutForm({
  bookingId,
  returnUrl,
  totalAmount,
  onPaymentSuccess,
  onPaymentError,
}: CheckoutFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);

    try {
      const { error } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}${returnUrl}`,
        },
      });

      if (error) {
        toast({
          title: 'Payment Failed',
          description: error.message || 'An error occurred during payment',
          variant: 'destructive',
        });
        onPaymentError?.(error);
      } else {
        onPaymentSuccess?.();
      }
    } catch (err) {
      const error = err as Error;
      toast({
        title: 'Payment Error',
        description: error.message || 'An unexpected error occurred',
        variant: 'destructive',
      });
      onPaymentError?.(error);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        <PaymentElement />
      </div>

      <Button
        type="submit"
        disabled={!stripe || isProcessing}
        className="w-full"
      >
        {isProcessing ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Processing...
          </>
        ) : (
          `Pay ${(totalAmount / 100).toLocaleString('en-AE', {
            style: 'currency',
            currency: 'AED',
          })}`
        )}
      </Button>

      <div className="text-sm text-muted-foreground text-center">
        <p>Your payment is secured by Stripe</p>
        <p className="mt-1">
          <img
            src="/stripe-badge.svg"
            alt="Stripe Secure"
            className="h-6 inline-block"
          />
        </p>
      </div>
    </form>
  );
}